import { DropResult } from "@hello-pangea/dnd";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  getGetDoclistQueryKey,
  useCreateCategoryFromItem,
  useReorderChatDoclistCategories,
  useReorderChatDoclistSections,
} from "~/api/chat/chat";
import { getListCustomReportsByCaseQueryKey } from "~/api/custom-reports/custom-reports";
import { DoclistTreeItem } from "~/api/model";
import { CategoryItem } from "~/features/chat/types";
import { findPreviousSectionId } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
export function useDndForSectionsAndCategories(
  items: DoclistTreeItem[],
  setItems: React.Dispatch<React.SetStateAction<DoclistTreeItem[]>>,
  filteredSections: DoclistTreeItem[],
  organizationId: string,
  projectId: string,
  isViewOnly: boolean,
  updatedAt: number | undefined,
  onUpdatedAtChange: (updatedAt: number) => void,
  onDoclistOutdated: () => void,
  categoryPlaceholders: CategoryItem[],
  setCategoryPlaceholders: React.Dispatch<React.SetStateAction<CategoryItem[]>>
) {
  const { mutateAsync: reorderSections } = useReorderChatDoclistSections();
  const { mutateAsync: reorderCategories } = useReorderChatDoclistCategories();
  const { mutateAsync: createCategory } = useCreateCategoryFromItem();
  const queryClient = useQueryClient();
  const handleDragEnd = async (result: DropResult) => {
    if (isViewOnly || !updatedAt) return;

    const { destination, source, type } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (!items) return;
    if (type === "category") {
      trackEvent({
        eventName: "app/doc_list/move_category/drag",
      });
      const newItems = Array.from(items);
      const [removed] = newItems.splice(source.index, 1);
      const categoryId = removed.category.id;
      const prevCategoryId =
        destination.index > 0
          ? newItems[destination.index - 1].category.id
          : null;
      newItems.splice(destination.index, 0, removed);
      setItems([...newItems]);
      try {
        const response = await reorderCategories({
          organizationId,
          projectId,
          data: {
            categoryId,
            target: {
              prevCategoryId,
            },
            updatedAt: updatedAt,
          },
        });
        onUpdatedAtChange(response.updatedAt);
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, projectId),
        });
        queryClient.invalidateQueries({
          queryKey: getListCustomReportsByCaseQueryKey(
            organizationId,
            projectId
          ),
        });
      } catch (error) {
        setItems([...items]);
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            onDoclistOutdated();
          }
        }
        logger.error("Failed to reorder categories:", error);
      }
      return;
    }

    trackEvent({
      eventName: "app/doc_list/move_document/drag",
    });

    const newItems = items.map((item) => ({
      ...item,
      sections: [...item.sections],
    }));

    const sourceCategory = items.find(
      (item) => `category-${item.category.id}` === source.droppableId
    );

    const sourceItem = newItems.find(
      (item) => `category-${item.category.id}` === source.droppableId
    )!;
    if (!sourceCategory || !sourceItem) return;
    const sourceFilteredItem = filteredSections.find(
      (item) => `category-${item.category.id}` === source.droppableId
    );

    if (!sourceFilteredItem || !sourceFilteredItem.sections[source.index])
      return;

    const draggedSectionId = sourceFilteredItem.sections[source.index].id;
    const sourceSection = sourceItem.sections.find(
      (sect) => sect.id === draggedSectionId
    );
    if (!sourceSection) return;
    const sourceIndex = sourceItem.sections.indexOf(sourceSection);

    const [removed] = sourceItem.sections.splice(sourceIndex, 1);

    // If there are no sections left in the category, delete the category
    if (sourceItem.sections.length === 0) {
      const categoryIndex = newItems.findIndex(
        (item) => item.categoryId === sourceItem.categoryId
      );
      if (categoryIndex !== -1) {
        newItems.splice(categoryIndex, 1);
      }
    }

    // Check if the destination is a new category placeholder
    const isNewCategoryTarget =
      destination.droppableId.startsWith("new-category-");
    if (isNewCategoryTarget) {
      const categoryPlaceholder = categoryPlaceholders.find(
        (placeholder) =>
          `new-category-${placeholder.id}` === destination.droppableId
      );
      if (!categoryPlaceholder) return;
      setItems([...newItems]);
      try {
        const response = await createCategory({
          organizationId,
          projectId,
          data: {
            source: {
              type: "section",
              id: sourceSection.id,
              title: categoryPlaceholder.name,
              color: categoryPlaceholder.color,
            },
            updatedAt,
          },
        });
        onUpdatedAtChange(response.updatedAt);
        setItems([response.node, ...newItems]);
        queryClient.invalidateQueries({
          queryKey: getGetDoclistQueryKey(organizationId, projectId),
        });
        queryClient.invalidateQueries({
          queryKey: getListCustomReportsByCaseQueryKey(
            organizationId,
            projectId
          ),
        });
        setCategoryPlaceholders((prev) =>
          prev.filter((p) => p.id !== categoryPlaceholder.id)
        );
      } catch (error) {
        setItems([...items]);
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            onDoclistOutdated();
          }
        }
        logger.error("Failed to create category:", error);
      }
      return;
    }

    const destCategory = items.find(
      (item) => `category-${item.category.id}` === destination.droppableId
    );

    const destItem = newItems.find(
      (item) => `category-${item.category.id}` === destination.droppableId
    )!;

    if (!destCategory || !destItem) return;

    destItem.sections.splice(destination.index, 0, {
      ...removed,
      categoryId: destCategory.category.id,
    });
    setItems([...newItems]);
    try {
      const prevId = findPreviousSectionId(
        newItems,
        String(destCategory.category.id),
        destination.index
      );

      const response = await reorderSections({
        organizationId,
        projectId,
        data: {
          sectionId: draggedSectionId,
          target: {
            prevId,
            folderCategoryId: destCategory.category.id,
          },
          updatedAt: updatedAt,
        },
      });
      onUpdatedAtChange(response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, projectId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, projectId),
      });
    } catch (error) {
      setItems([...items]);
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          onDoclistOutdated();
        }
      }
      logger.error("Failed to reorder sections:", error);
    }
  };

  return {
    handleDragEnd,
  };
}
